import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../../globals/components/Navbar";
import {
  Box,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  Button,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { apiClient } from "../../../api/httpClient";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, Tooltip } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useSelector } from "react-redux";
import TablePaginationActions from "../../../globals/components/TablePaginationActions";
import { toIssue } from "../../../globals/utils/homeHelpers";

const ManuallySignedIndemnities = () => {
  const [loading, setLoading] = useState(false);
  const [bonds, setBonds] = useState([]);
  const [selectedBond, setSelectedBond] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [lawFirmBondAllowList, setLawFirmBondAllowList] = useState([]);

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [subsPage, setSubsPage] = useState(0);
  const [subsRowsPerPage, setSubsRowsPerPage] = useState(10);
  const [subsOrderBy, setSubsOrderBy] = useState("created_at");
  const [subsOrder, setSubsOrder] = useState("desc");
  const [subsStatusTab, setSubsStatusTab] = useState("pending");
  const [filters, setFilters] = useState({
    bondNumber: [],
    bondType: [],
    bondSubCategory: [],
    principal: [],
    estate: [],
    lawFirm: [],
    status: [],
    startDate: "",
    endDate: "",
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationResults, setValidationResults] = useState([]);
  const hasAnyValidationErrors = useMemo(
    () => validationResults.some((f) => f.issues.some((i) => i.status === "fail")),
    [validationResults]
  );
  const hasBlockingErrors = useMemo(
    () => validationResults.some((f) => f.issues.some((i) => i.status === "fail" && i.key !== "pii")),
    [validationResults]
  );
  const hasPiiWarningsOnly = useMemo(
    () => hasAnyValidationErrors && !hasBlockingErrors,
    [hasAnyValidationErrors, hasBlockingErrors]
  );
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const [detailsRecord, setDetailsRecord] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  // Manage files (upload/replace/delete) on existing indemnity record
  const [fileManageOpen, setFileManageOpen] = useState(false);
  const [fileManageSlot, setFileManageSlot] = useState(""); // internal: "" lets server pick the first available empty position
  const [fileManageFile, setFileManageFile] = useState(null);
  const [fileManageSubmitting, setFileManageSubmitting] = useState(false);
  const [deleteFileOpen, setDeleteFileOpen] = useState(false);
  const [deleteFileSlot, setDeleteFileSlot] = useState(null);
  const [deleteFileSubmitting, setDeleteFileSubmitting] = useState(false);
  const auth = useSelector((s) => s.auth);
  const lawfirmState = useSelector((s) => s.lawfirm || {});
  const canSendToLiberty = auth?.role === "internal_user" || auth?.role === "super_user";

  // Internal/Super: load all bonds from archive. Law firm: list-lawfirm-bonds. Guardian/pg_contact: list-guardian-bonds.
  useEffect(() => {
    const isLawFirm = auth?.role === "law_firm_user" || auth?.role === "law_firm_admin";
    const isGuardian = auth?.role === "professional_guardian" || auth?.role === "pg_contact";
    if (isLawFirm || isGuardian) return;

    const loadBonds = async () => {
      try {
        setLoading(true);
        const data = await apiClient("/archive-bonds", "GET");
        setBonds(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Failed to load archive bonds", e);
        setBonds([]);
      } finally {
        setLoading(false);
      }
    };
    loadBonds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.role]);

  useEffect(() => {
    const loadBondsByRole = async () => {
      const isInternalOrSuper =
        auth?.role === "internal_user" || auth?.role === "super_user";
      if (isInternalOrSuper) {
        setLawFirmBondAllowList([]);
        return;
      }

      const isLawFirm = auth?.role === "law_firm_user" || auth?.role === "law_firm_admin";
      const isGuardian = auth?.role === "professional_guardian" || auth?.role === "pg_contact";
      const lawfirmId = lawfirmState?.lawfirmId;
      const pgId = auth?.pg_id;

      if (isLawFirm) {
        if (!lawfirmId) {
          setLawFirmBondAllowList([]);
          setBonds([]);
          return;
        }
        try {
          setLoading(true);
          const list = await apiClient(`/list-lawfirm-bonds/${lawfirmId}/`, "GET");
          const arr = Array.isArray(list) ? list : [];
          setLawFirmBondAllowList(arr);
          setBonds(arr);
        } catch (e) {
          console.error("Failed to load law firm bonds", e);
          setLawFirmBondAllowList([]);
          setBonds([]);
        } finally {
          setLoading(false);
        }
        return;
      }

      if (isGuardian) {
        if (!pgId) {
          setLawFirmBondAllowList([]);
          setBonds([]);
          return;
        }
        try {
          setLoading(true);
          const list = await apiClient(`/list-guardian-bonds/${pgId}/`, "GET");
          const arr = Array.isArray(list) ? list : [];
          setLawFirmBondAllowList(arr);
          setBonds(arr);
        } catch (e) {
          console.error("Failed to load guardian bonds", e);
          setLawFirmBondAllowList([]);
          setBonds([]);
        } finally {
          setLoading(false);
        }
        return;
      }

      setLawFirmBondAllowList([]);
    };

    loadBondsByRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.role, auth?.pg_id, lawfirmState?.lawfirmId]);

  const normalizeBondKey = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  useEffect(() => {
    const loadSubmissions = async () => {
      try {
        setLoadingSubmissions(true);
        // determine filters by role context
        const isInternalOrSuper = auth?.role === "internal_user" || auth?.role === "super_user";
        const params = {};
        if (!isInternalOrSuper) {
          const lawfirmId = lawfirmState?.lawfirmId;
          const pgId = auth?.pg_id;
          if (lawfirmId) params.lawfirm_id = lawfirmId;
          else if (pgId) params.professional_guardian_id = pgId;
          else if (auth?.user?.id) params.requester_id = String(auth.user.id);
        }
        const data = await apiClient("/manually-signed-indemnities/", "GET", {}, params);
        const rows = Array.isArray(data) ? data : [];
        // enrich with bond details when available (normalize keys to strings for safe matching)
        const byBondId = new Map(bonds.map((b) => [String(b?.bond_id), b]));
        const extractBondIdFromRecord = (rec) => {
          if (!rec) return null;
          const urls = [rec.file_url_1, rec.file_url_2, rec.file_url_3, rec.file_url_4].filter(Boolean);
          for (const u of urls) {
            try {
              const m = String(u).match(/bond_(\d+)_/i);
              if (m && m[1]) return String(m[1]);
            } catch {}
          }
          return null;
        };
        const mapped = rows.map((r) => {
          const key = r?.bond_id != null ? String(r.bond_id) : extractBondIdFromRecord(r);
          const b = key ? (byBondId.get(String(key)) || {}) : {};
          return {
            ...r,
            bond_number: b.bond_number,
            bond_type: b.bond_type,
            bond_sub_category: b.bond_sub_category,
            principals: Array.isArray(b.principals)
              ? b.principals
              : (b.principals || "")
                  .split(",")
                  .map((p) => p.trim())
                  .filter(Boolean),
            estate_name: b.name,
            law_firm_name: b.law_firm_name,
            created_at: r.created_at,
          };
        });
        setSubmissions(mapped);
      } catch (e) {
        console.error("Failed to load indemnities", e);
        setSubmissions([]);
      } finally {
        setLoadingSubmissions(false);
      }
    };
    loadSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bonds, auth?.pg_id, auth?.user?.id, lawfirmState?.lawfirmId]);

  const options = useMemo(() => {
    const allowSet = new Set(
      Array.isArray(lawFirmBondAllowList)
        ? lawFirmBondAllowList.flatMap((b) =>
            [normalizeBondKey(b?.bond_number), normalizeBondKey(b?.bond_id)].filter(Boolean)
          )
        : []
    );

    // Deduplicate by bond_id; normalize to strings for stable keys
    const map = new Map();
    for (const b of bonds || []) {
      if (!b || !b.bond_id || !b.bond_number) continue;
      if (
        allowSet.size &&
        !allowSet.has(normalizeBondKey(b.bond_id)) &&
        !allowSet.has(normalizeBondKey(b.bond_number))
      ) {
        continue;
      }
      const key = String(b.bond_id);
      if (!map.has(key)) {
        map.set(key, { label: String(b.bond_number), value: key });
      }
    }
    return Array.from(map.values());
  }, [bonds, lawFirmBondAllowList]);

  const handleSelect = (_e, value) => {
    if (!value) {
      setSelectedBond(null);
      return;
    }
    const bond = bonds.find((b) => b.bond_id === value.value || b.bond_number === value.label);
    setSelectedBond(bond || null);
  };

  const handleTabChange = (_e, v) => {
    setTabIndex(v);
  };

  const formatCurrency = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return "-";
    return `$${num.toLocaleString()}`;
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const getViewBondUrl = (bond) => {
    if (!bond) return "";
    const type = String(bond.bond_type || "").trim();
    const id = bond.bond_id;
    if (!id) return "";
    if (type === "VA") return `/vermost-va-bond-update/${id}`;
    if (type === "Trustee") return `/vermost-trustee-bond-update/${id}`;
    if (type === "Probate") return `/vermost-probate-bond-update/${id}`;
    if (type === "Blanket") return `/vermost-blanket-bond-update/${id}`;
    return `/vermost-guardianship-bond-update/${id}`;
  };

  const handleFilesSelected = (event) => {
    const all = Array.from(event.target.files || []);
    const files = all.slice(0, 4); // API accepts up to 4
    const pdfsOnly = files.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    if (pdfsOnly.length !== files.length) {
      toast.error("Only PDF files are allowed.");
    }
    setSelectedFiles(pdfsOnly);
  };

  const handleClearFiles = () => {
    setSelectedFiles([]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleViewFile = (file) => {
    try {
      const url = URL.createObjectURL(file);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error("Preview failed", e);
      toast.error("Unable to preview file.");
    }
  };

  // Details dialog helpers (All Uploaded Indemnities)
  const openDetailsForRow = (row) => {
    setDetailsRow(row);
    setDetailsRecord(null);
    (async () => {
      try {
        if (!row?.manually_signed_indemnity_id) {
          setDetailsOpen(true);
          return;
        }
        const rec = await apiClient(`/manually-signed-indemnities/${row.manually_signed_indemnity_id}/`, "GET");
        setDetailsRecord(rec);
      } catch (e) {
        console.error("Failed to load indemnity record", e);
      } finally {
        setDetailsOpen(true);
      }
    })();
  };

  const refreshDetailsRecord = async (id) => {
    if (!id) return;
    try {
      const rec = await apiClient(`/manually-signed-indemnities/${id}/`, "GET");
      setDetailsRecord(rec);
    } catch (e) {
      console.error("Failed to refresh indemnity record", e);
    }
  };

  const openFileManageDialog = (slot = "") => {
    setFileManageSlot(slot ? String(slot) : "");
    setFileManageFile(null);
    setFileManageOpen(true);
  };

  const handleUploadOrReplaceFile = async () => {
    const id = detailsRow?.manually_signed_indemnity_id;
    if (!id) {
      toast.error("Missing indemnity id.");
      return;
    }
    if (!fileManageFile) {
      toast.error("Please choose a file to upload.");
      return;
    }
    try {
      setFileManageSubmitting(true);
      const form = new FormData();
      form.append("file", fileManageFile);
      if (fileManageSlot) form.append("slot", String(fileManageSlot));
      await apiClient(
        `/manually-signed-indemnities/${id}/files/`,
        "PATCH",
        form,
        {},
        { "Content-Type": "multipart/form-data" }
      );
      toast.success("File uploaded");
      setFileManageOpen(false);
      await refreshDetailsRecord(id);
    } catch (e) {
      console.error("Upload/replace file failed", e);
      toast.error(e?.error || "Failed to upload file.");
    } finally {
      setFileManageSubmitting(false);
    }
  };

  const openDeleteFileDialog = (slot) => {
    setDeleteFileSlot(slot);
    setDeleteFileOpen(true);
  };

  const handleDeleteFileSlot = async () => {
    const id = detailsRow?.manually_signed_indemnity_id;
    if (!id) {
      toast.error("Missing indemnity id.");
      return;
    }
    if (!deleteFileSlot) {
      toast.error("Unable to delete file.");
      return;
    }
    try {
      setDeleteFileSubmitting(true);
      await apiClient(
        `/manually-signed-indemnities/${id}/files/`,
        "DELETE",
        {},
        { slot: String(deleteFileSlot) }, // internal
        {}
      );
      toast.success("File deleted");
      setDeleteFileOpen(false);
      setDeleteFileSlot(null);
      await refreshDetailsRecord(id);
    } catch (e) {
      console.error("Delete file failed", e);
      toast.error(e?.error || "Failed to delete file.");
    } finally {
      setDeleteFileSubmitting(false);
    }
  };

  const handleSendToLiberty = async () => {
    if (!detailsRow?.manually_signed_indemnity_id) {
      toast.error("Unable to approve: missing indemnity id.");
      return;
    }
    try {
      setApproveSubmitting(true);
      const id = detailsRow.manually_signed_indemnity_id;
      const updated = await apiClient(
        `/manually-signed-indemnities/${id}/approve/`,
        "POST",
        { status: "approved" },
        {},
        {}
      );
      toast.success("Indemnity sent to Liberty (approved) successfully.");
      // Update local state with the response where available
      setSubmissions((prev) =>
        (Array.isArray(prev) ? prev : []).map((r) =>
          r.manually_signed_indemnity_id === id
            ? {
                ...r,
                ...(updated || {}),
                status: updated?.status || "approved",
                approved_at: updated?.approved_at ?? r.approved_at,
              }
            : r
        )
      );
      setDetailsRow((prev) =>
        prev && prev.manually_signed_indemnity_id === id
          ? {
              ...prev,
              ...(updated || {}),
              status: updated?.status || "approved",
              approved_at: updated?.approved_at ?? prev.approved_at,
            }
          : prev
      );
      setDetailsOpen(false);
    } catch (e) {
      console.error("Approve indemnity failed", e);
      toast.error(e?.error || "Failed to send indemnity to Liberty.");
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleOpenRejectDialog = () => {
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleSubmitReject = async () => {
    if (!detailsRow?.manually_signed_indemnity_id || !rejectReason.trim()) {
      toast.error("Please provide a rejection reason.");
      return;
    }
    try {
      setRejectSubmitting(true);
      const id = detailsRow.manually_signed_indemnity_id;
      await apiClient(
        `/manually-signed-indemnities/${id}/reject/`,
        "POST",
        { reason: rejectReason.trim(), status: "rejected" },
        {},
        {}
      );
      toast.success("Indemnity rejected successfully.");
      // Update local state so table and details reflect the new status
      setSubmissions((prev) =>
        (Array.isArray(prev) ? prev : []).map((r) =>
          r.manually_signed_indemnity_id === id ? { ...r, status: "rejected" } : r
        )
      );
      setDetailsRow((prev) =>
        prev && prev.manually_signed_indemnity_id === id ? { ...prev, status: "rejected" } : prev
      );
      setRejectDialogOpen(false);
    } catch (e) {
      console.error("Reject indemnity failed", e);
      toast.error(e?.error || "Failed to reject indemnity.");
    } finally {
      setRejectSubmitting(false);
    }
  };

  const [processingOpen, setProcessingOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const handleSubmit = async () => {
    if (!selectedBond?.bond_id) {
      toast.error("Please select a bond number first.");
      return;
    }
    if (!selectedFiles.length) {
      toast.error("Please select at least one PDF to upload.");
      return;
    }
    setIsSubmitting(true);
    setProcessingOpen(true);
    try {
      const form = new FormData();
      selectedFiles.forEach((f) => form.append("files", f));
      form.append("use_hg_sales_key", "false");
      const res = await apiClient("/indemnity-compliance-check/", "POST", form, {}, {});
      // Normalize results
      const resultsArr = Array.isArray(res?.results) ? res.results : [];
      const mapped = resultsArr.map((r, idx) => {
        const name = r?.file_name || selectedFiles[idx]?.name || `file_${idx + 1}.pdf`;
        const base = toIssue(name, r);
        const sz = selectedFiles[idx]?.size ? Math.max(1, Math.round(selectedFiles[idx].size / 1024)) : null;
        return { ...base, sizeKb: sz };
      });
      setValidationResults(mapped);
      setValidationOpen(true);
    } catch (e) {
      console.error("Compliance check failed", e);
      toast.error(e?.error || "Failed to analyze indemnity PDF(s).");
    } finally {
      setIsSubmitting(false);
      setProcessingOpen(false);
    }
  };

  const handleFinalizeSubmit = async () => {
    if (!selectedBond?.bond_id || !selectedFiles.length) return;
    setIsFinalizing(true);
    try {
      const form = new FormData();
      selectedFiles.slice(0, 4).forEach((f) => form.append("files", f));
      form.append("bond_id", String(selectedBond.bond_id));
      const lawfirmId = lawfirmState?.lawfirmId;
      const pgId = auth?.pg_id;
      if (lawfirmId) form.append("lawfirm_id", String(lawfirmId));
      if (pgId) form.append("professional_guardian_id", String(pgId));
      if (auth?.user?.id) form.append("requester_id", String(auth.user.id));
      form.append("status", "pending");
      const created = await apiClient("/manually-signed-indemnities/", "POST", form, {}, {});
      
      // Also mark indemnity as received
      if (selectedBond?.bond_id && selectedBond?.bond_type) {
        try {
          // Map bond type to API format (Probate -> probate, VA -> va, etc.)
          const bondTypeMap = {
            "Probate": "probate",
            "Guardianship": "guardianship",
            "VA": "va",
            "Trustee": "trustee",
            "Blanket": "blanket"
          };
          const apiBondType = bondTypeMap[selectedBond.bond_type] || String(selectedBond.bond_type).toLowerCase();
          
          await apiClient("/set-indemnity-received/", "POST", {
            bond_id: selectedBond.bond_id,
            bond_type: apiBondType,
          });
        } catch (e) {
          console.error("Failed to mark indemnity as received", e);
          // Don't show error to user, just log it - the documents were submitted successfully
        }
      }
      
      toast.success("Indemnity document(s) submitted successfully.");
      // reflect in table
      const row = {
        ...created,
        bond_number: selectedBond.bond_number,
        bond_type: selectedBond.bond_type,
        bond_sub_category: selectedBond.bond_sub_category,
        estate_name: selectedBond.name,
        law_firm_name: selectedBond.law_firm_name,
        created_at: created?.created_at || new Date().toISOString(),
      };
      setSubmissions((prev) => [row, ...prev]);
      setSelectedFiles([]);
      setValidationOpen(false);
    } catch (e) {
      console.error("Submit failed", e);
      toast.error(e?.error || "Failed to submit indemnity files.");
    } finally {
      setIsFinalizing(false);
    }
  };

  const baseFilteredSubmissions = useMemo(() => {
    const rows = Array.isArray(submissions) ? submissions : [];
    let result = [...rows];

    if (filters.bondNumber.length) {
      result = result.filter((r) => {
        const num = r.bond_number ?? r.bond?.bond_number;
        return filters.bondNumber.some((f) => f.value === num);
      });
    }

    if (filters.bondType.length) {
      result = result.filter((r) => {
        const type = r.bond_type ?? r.bond?.bond_type;
        return filters.bondType.some((f) => f.value === type);
      });
    }

    if (filters.bondSubCategory.length) {
      result = result.filter((r) => {
        const sub = r.bond_sub_category ?? r.bond?.bond_sub_category;
        return filters.bondSubCategory.includes(sub);
      });
    }

    if (filters.principal.length) {
      result = result.filter((r) => {
        const principals = Array.isArray(r.principals)
          ? r.principals
          : (r.principals || "")
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean);
        return filters.principal.some((x) =>
          principals.some((p) => p.toLowerCase() === x.label.toLowerCase())
        );
      });
    }

    if (filters.estate.length) {
      result = result.filter((r) => {
        const estate = r.estate_name ?? r.name ?? r.bond?.name;
        return filters.estate.some((f) => f.label === estate);
      });
    }

    if (filters.lawFirm.length) {
      result = result.filter((r) => {
        const firm = r.law_firm_name ?? r.bond?.law_firm_name;
        return filters.lawFirm.some((f) => f.label === firm);
      });
    }

    if (filters.status.length) {
      result = result.filter((r) => {
        const st = String(r.status ?? r.bond?.status ?? "").toLowerCase();
        return filters.status.some((s) => s.toLowerCase() === st);
      });
    }

    if (filters.startDate) {
      const start = new Date(filters.startDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter((r) => {
        const d = new Date(r.created_at ?? r.submitted_at ?? r.bond?.created_at);
        if (Number.isNaN(d.getTime())) return false;
        return d >= start;
      });
    }

    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((r) => {
        const d = new Date(r.created_at ?? r.submitted_at ?? r.bond?.created_at);
        if (Number.isNaN(d.getTime())) return false;
        return d <= end;
      });
    }

    return result;
  }, [submissions, filters]);

  const sortedSubmissions = useMemo(() => {
    const rows = Array.isArray(baseFilteredSubmissions) ? baseFilteredSubmissions : [];
    const getCell = (r, key) => {
      // support flatter or nested structures
      if (key === "bond_number") return r.bond_number ?? r.bond?.bond_number ?? "-";
      if (key === "bond_type") return r.bond_type ?? r.bond?.bond_type ?? "-";
      if (key === "bond_sub_category") return r.bond_sub_category ?? r.bond?.bond_sub_category ?? "-";
      if (key === "estate_name") return r.estate_name ?? r.name ?? r.bond?.name ?? "-";
      if (key === "law_firm_name") return r.law_firm_name ?? r.bond?.law_firm_name ?? "-";
      if (key === "principals") {
        const list = Array.isArray(r.principals)
          ? r.principals
          : (r.principals || "")
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean);
        return list.join(", ");
      }
      if (key === "status") return r.status ?? r.bond?.status ?? "-";
      if (key === "created_at") return r.created_at ?? r.submitted_at ?? r.bond?.created_at ?? null;
      return r[key];
    };
    const list = [...rows];
    if (subsOrderBy === "created_at") {
      return list.sort((a, b) => {
        const da = new Date(getCell(a, "created_at") || 0).getTime();
        const db = new Date(getCell(b, "created_at") || 0).getTime();
        return subsOrder === "asc" ? da - db : db - da;
      });
    }
    return list.sort((a, b) => {
      const va = String(getCell(a, subsOrderBy) || "").toLowerCase();
      const vb = String(getCell(b, subsOrderBy) || "").toLowerCase();
      return subsOrder === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [baseFilteredSubmissions, subsOrderBy, subsOrder]);

  const getRowStatus = (row) =>
    String(row?.status ?? row?.bond?.status ?? "")
      .trim()
      .toLowerCase();

  const renderStatusChip = (row) => {
    const st = getRowStatus(row);
    const label = st ? st.charAt(0).toUpperCase() + st.slice(1) : "-";
    if (st === "pending") return <Chip size="small" color="warning" label={label} />;
    if (st === "rejected") return <Chip size="small" color="error" label={label} />;
    if (st === "approved" || st === "completed") return <Chip size="small" color="success" label={label} />;
    return <Chip size="small" variant="outlined" label={label} />;
  };

  const pendingCount = useMemo(
    () => sortedSubmissions.filter((r) => getRowStatus(r) === "pending").length,
    [sortedSubmissions]
  );

  const approvedCount = useMemo(() => {
    return sortedSubmissions.filter((r) => {
      const st = getRowStatus(r);
      return st === "completed" || st === "approved";
    }).length;
  }, [sortedSubmissions]);

  const rejectedCount = useMemo(
    () => sortedSubmissions.filter((r) => getRowStatus(r) === "rejected").length,
    [sortedSubmissions]
  );

  const filteredSubmissions = useMemo(() => {
    if (subsStatusTab === "pending") {
      return sortedSubmissions.filter((r) => getRowStatus(r) === "pending");
    }
    if (subsStatusTab === "approved") {
      return sortedSubmissions.filter((r) => {
        const st = getRowStatus(r);
        return st === "completed" || st === "approved";
      });
    }
    if (subsStatusTab === "rejected") {
      return sortedSubmissions.filter((r) => getRowStatus(r) === "rejected");
    }
    return sortedSubmissions;
  }, [sortedSubmissions, subsStatusTab]);

  const submissionFilterOptions = useMemo(() => {
    const rows = Array.isArray(submissions) ? submissions : [];
    const bondNumbers = new Set();
    const bondTypes = new Set();
    const bondSubCategories = new Set();
    const principals = new Set();
    const estates = new Set();
    const lawFirms = new Set();
    const statuses = new Set();

    rows.forEach((r) => {
      const num = r.bond_number ?? r.bond?.bond_number;
      if (num) bondNumbers.add(String(num));

      const type = r.bond_type ?? r.bond?.bond_type;
      if (type) bondTypes.add(String(type));

      const sub = r.bond_sub_category ?? r.bond?.bond_sub_category;
      if (sub) bondSubCategories.add(String(sub));

      const estate = r.estate_name ?? r.name ?? r.bond?.name;
      if (estate) estates.add(String(estate));

      const firm = r.law_firm_name ?? r.bond?.law_firm_name;
      if (firm) lawFirms.add(String(firm));

      const status = r.status ?? r.bond?.status;
      if (status) statuses.add(String(status));

      const principalList = Array.isArray(r.principals)
        ? r.principals
        : (r.principals || "")
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);
      principalList.forEach((p) => principals.add(p));
    });

    const toOptions = (set) => Array.from(set).map((v) => ({ label: v, value: v }));

    return {
      bondNumbers: toOptions(bondNumbers),
      bondTypes: toOptions(bondTypes),
      bondSubCategories: Array.from(bondSubCategories),
      principals: toOptions(principals),
      estates: toOptions(estates),
      lawFirms: toOptions(lawFirms),
      statuses: Array.from(statuses),
    };
  }, [submissions]);

  const handleSubsSort = (column) => {
    const isAsc = subsOrderBy === column && subsOrder === "asc";
    setSubsOrder(isAsc ? "desc" : "asc");
    setSubsOrderBy(column);
  };

  const subsColumns = [
    "bond_number",
    "bond_type",
    "bond_sub_category",
    "principals",
    "estate_name",
    "law_firm_name",
    "status",
    "created_at",
  ];

  return (
    <>
      <Navbar />
      <Box sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Upload Indemnity
            </Typography>
            <span style={{ fontSize: "14px", color: "#666" }}>
              Select a bond number to view key details and open the bond.
            </span>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Upload Indemnity" />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>All Uploaded Indemnities</span>
                  {pendingCount > 0 ? (
                    <Chip size="small" label={pendingCount} color="success" />
                  ) : null}
                </Box>
              }
            />
          </Tabs>
        </Box>

        {tabIndex === 0 ? (
          <>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                background: (theme) =>
                  theme.palette.mode === "light"
                    ? "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)"
                    : theme.palette.background.paper,
                border: "1px solid rgba(37, 99, 235, 0.2)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Autocomplete
                  options={options}
                  loading={loading}
                  onChange={handleSelect}
                  getOptionLabel={(o) => o?.label ?? ""}
                  isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                  renderOption={(props, option) => (
                    <li {...props} key={`${option?.value}`}>
                      {option?.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Bond Number"
                      placeholder="Start typing to search bond numbers"
                      slotProps={{ input: { ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}}
                    />
                  )}
                />
              </Box>
            </Paper>

            {selectedBond ? (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  background: "#fff",
                  border: "1px solid rgba(37, 99, 235, 0.2)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#333333" }}>
                      {selectedBond.bond_number || "—"}
                    </Typography>
                    {selectedBond.status ? (
                      <Chip
                        size="small"
                        label={selectedBond.status}
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    ) : null}
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const url = getViewBondUrl(selectedBond);
                      if (url) window.open(url, "_blank");
                    }}
                    sx={{ textTransform: "none", color: "#2563eb", borderColor: "rgba(37, 99, 235, 0.3)" }}
                  >
                    View Bond
                  </Button>
                </Box>
                <Divider sx={{ my: 1.5, borderColor: "rgba(37, 99, 235, 0.12)" }} />
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Bond Type
                    </Typography>
                    <Typography variant="body2">{selectedBond.bond_type || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sub Category
                    </Typography>
                    <Typography variant="body2">{selectedBond.bond_sub_category || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Estate / Ward / Trust / Beneficiary
                    </Typography>
                    <Typography variant="body2">{selectedBond.name || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Law Firm
                    </Typography>
                    <Typography variant="body2">{selectedBond.law_firm_name || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Bond Amount
                    </Typography>
                    <Typography variant="body2">{formatCurrency(selectedBond.bond_amount)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body2">{formatDate(selectedBond.created_at)}</Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2, borderColor: "rgba(37, 99, 235, 0.12)" }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Upload Indemnity PDFs
                  </Typography>
                  {(() => {
                    const isProbateUnder50k = selectedBond?.bond_type === "Probate" && (parseFloat(selectedBond?.bond_amount) || 0) <= 50000;
                    return (
                      <>
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={isProbateUnder50k}
                            sx={{ textTransform: "none", color: "#2563eb", borderColor: "rgba(37, 99, 235, 0.3)" }}
                          >
                            Choose File(s)
                            <input
                              type="file"
                              hidden
                              accept="application/pdf"
                              multiple
                              onChange={handleFilesSelected}
                            />
                          </Button>
                    {selectedFiles.length > 0 && (
                      <Button
                        variant="text"
                        onClick={handleClearFiles}
                        sx={{ textTransform: "none", color: "text.secondary" }}
                      >
                        Clear selection
                      </Button>
                    )}
                  </Box>
                  {isProbateUnder50k && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This Indemnity Agreement does not need to be signed or submitted to Probate Bond Pros.
                    </Typography>
                  )}
                  {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedFiles.length} file(s) selected
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {selectedFiles.map((f, i) => {
                          const sizeKb = Math.max(1, Math.round((f.size || 0) / 1024));
                          return (
                            <Paper
                              key={`${f.name}-${i}`}
                              elevation={0}
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                border: "1px solid rgba(37, 99, 235, 0.18)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <PictureAsPdfIcon sx={{ color: "error.main" }} />
                                <Box>
                                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                                    {f.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {sizeKb} KB
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Tooltip title="View">
                                  <IconButton size="small" onClick={() => handleViewFile(f)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton size="small" color="error" onClick={() => handleRemoveFile(i)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!selectedFiles.length || isSubmitting || isProbateUnder50k}
                    >
                      {isSubmitting ? "Uploading..." : "Submit"}
                    </Button>
                  </Box>
                      </>
                    );
                  })()}
                </Box>
              </Paper>
            ) : null}

            <Dialog open={processingOpen} slotProps={{ paper: { sx: { textAlign: "center", p: 3 } }}}>
              <DialogContent>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Processing Your Submission
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Thank you for submitting the Indemnity Agreement. Please wait a
                  moment as the Indemnity Agreement is being screened for submission.
                </Typography>
              </DialogContent>
            </Dialog>

            <Dialog
              open={validationOpen}
              onClose={() => setValidationOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {hasBlockingErrors ? (
                  <ErrorOutlineIcon color="error" />
                ) : hasPiiWarningsOnly ? (
                  <WarningAmberIcon sx={{ color: "warning.main" }} />
                ) : (
                  <CheckCircleIcon color="success" />
                )}
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Indemnity Validation Report
                </Typography>
                <Chip size="small" label="Probate Bond Pros AI" sx={{ ml: 1 }} />
              </DialogTitle>
              <DialogContent dividers sx={{ p: 2.5 }}>
                {hasBlockingErrors ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    The validation agent found issues that need attention before submitting the file to Probate Bond Pros. Close this report and re-upload corrected PDFs.
                  </Alert>
                ) : hasPiiWarningsOnly ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    PII exposure might be possible—please double-check before submitting. You may proceed if appropriate.
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    All checks passed. You can safely proceed to submit these document(s).
                  </Alert>
                )}
                <Stack spacing={1.25}>
                  {validationResults.map((file, idx) => {
                    const hasBlocking = file.issues.some((i) => i.status === "fail" && i.key !== "pii");
                    const hasPiiOnly = file.issues.some((i) => i.status === "fail" && i.key === "pii");
                    const borderColor = hasBlocking
                      ? "1px solid rgba(220,38,38,0.35)"
                      : hasPiiOnly
                        ? "1px solid rgba(237,108,2,0.35)"
                        : "1px solid rgba(16,185,129,0.35)";
                    const iconColor = hasBlocking ? "error.main" : hasPiiOnly ? "warning.main" : "success.main";
                    return (
                      <Paper
                        key={`${file.name}-${idx}`}
                        elevation={0}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          border: borderColor,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <PictureAsPdfIcon sx={{ color: iconColor }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {file.sizeKb} KB
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            {file.issues.map((iss, i) => {
                              const isPiiWarning = iss.key === "pii" && iss.status === "fail";
                              const chipColor = iss.status === "fail" ? (isPiiWarning ? "warning" : "error") : "success";
                              const chipLabel = iss.status === "fail"
                                ? (isPiiWarning ? `${iss.title}: Warning` : `${iss.title}: Needs fix`)
                                : `${iss.title}: OK`;
                              return (
                                <Chip
                                  key={`${iss.key}-${i}`}
                                  size="small"
                                  color={chipColor}
                                  variant="outlined"
                                  label={chipLabel}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                        {file.issues.map((iss, i) => {
                          const isPiiWarning = iss.key === "pii" && iss.status === "fail";
                          const issueColor = iss.status === "fail" ? (isPiiWarning ? "warning" : "error") : "success";
                          return (
                          <Accordion key={`${iss.key}-acc-${i}`} elevation={0} disableGutters sx={{ borderRadius: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {iss.status === "fail" ? (
                                  isPiiWarning ? (
                                    <WarningAmberIcon color="warning" fontSize="small" />
                                  ) : (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  )
                                ) : (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {iss.title}
                                </Typography>
                                <Chip
                                  size="small"
                                  color={issueColor}
                                  label={iss.summary}
                                  sx={{ ml: 0.5 }}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {iss.status === "fail" ? (
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      What we found
                                    </Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                      {iss.details.map((d, di) => (
                                        <Typography key={di} variant="body2">• {d}</Typography>
                                      ))}
                                    </Stack>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      How to fix
                                    </Typography>
                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                      {iss.nextSteps.map((n, ni) => (
                                        <Typography key={ni} variant="body2">• {n}</Typography>
                                      ))}
                                    </Stack>
                                  </Box>
                                </Box>
                              ) : (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    What we found
                                  </Typography>
                                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    {iss.details.map((d, di) => (
                                      <Typography key={di} variant="body2">• {d}</Typography>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                          );
                        })}
                      </Paper>
                    );
                  })}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ justifyContent: "space-between" }}>
                {hasBlockingErrors ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                      Close this report to address issues and upload corrected files.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="text" onClick={() => setValidationOpen(false)}>
                        Close
                      </Button>
                    </Box>
                  </>
                ) : hasPiiWarningsOnly ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                      Double-check for PII exposure; you may proceed if appropriate.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="text" onClick={() => setValidationOpen(false)}>
                        Close
                      </Button>
                      <Button variant="contained" color="warning" onClick={handleFinalizeSubmit} disabled={isFinalizing}>
                        {isFinalizing ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Submitting...</> : "Proceed"}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                      Documents are ready to submit.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="text" onClick={() => setValidationOpen(false)}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleFinalizeSubmit} disabled={isFinalizing}>
                        {isFinalizing ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Submitting...</> : "Submit"}
                      </Button>
                    </Box>
                  </>
                )}
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <>
          <Box
              sx={{
                mb: 1.5,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Tabs
                value={subsStatusTab}
                onChange={(_e, v) => {
                  setSubsStatusTab(v);
                  setSubsPage(0);
                }}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab
                  value="pending"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>Pending</span>
                      <Chip size="small" label={pendingCount} color="success" />
                    </Box>
                  }
                />
                <Tab
                  value="approved"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>Approved</span>
                      <Chip size="small" label={approvedCount} />
                    </Box>
                  }
                />
                <Tab
                  value="rejected"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>Rejected</span>
                      <Chip size="small" label={rejectedCount} color="error" />
                    </Box>
                  }
                />
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setFilterDialogOpen(true)}
              >
                Filters
              </Button>
            </Box>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    {subsColumns.map((col) => (
                      <TableCell
                        key={col}
                        sortDirection={subsOrderBy === col ? subsOrder : false}
                      >
                        <TableSortLabel
                          active={subsOrderBy === col}
                          direction={subsOrderBy === col ? subsOrder : "asc"}
                          onClick={() => handleSubsSort(col)}
                        >
                          {col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                  {loadingSubmissions ? (
                    <TableRow>
                      <TableCell colSpan={subsColumns.length + 1} align="center">
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : filteredSubmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={subsColumns.length + 1} align="center">
                        No Data Available
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSubmissions
                      .slice(subsPage * subsRowsPerPage, subsPage * subsRowsPerPage + subsRowsPerPage)
                      .map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.bond_number ?? row.bond?.bond_number ?? "-"}</TableCell>
                          <TableCell>{row.bond_type ?? row.bond?.bond_type ?? "-"}</TableCell>
                          <TableCell>{row.bond_sub_category ?? row.bond?.bond_sub_category ?? "-"}</TableCell>
                          <TableCell>
                            {Array.isArray(row.principals)
                              ? row.principals.join(", ")
                              : row.principals || "-"}
                          </TableCell>
                          <TableCell>{row.estate_name ?? row.name ?? row.bond?.name ?? "-"}</TableCell>
                          <TableCell>{row.law_firm_name ?? row.bond?.law_firm_name ?? "-"}</TableCell>
                          <TableCell>{renderStatusChip(row)}</TableCell>
                          <TableCell>
                            {formatDate(row.created_at ?? row.submitted_at ?? row.bond?.created_at)}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={() => openDetailsForRow(row)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
          ActionsComponent={TablePaginationActions}
              component="div"
              count={filteredSubmissions.length}
              page={subsPage}
              onPageChange={(_e, p) => setSubsPage(p)}
              rowsPerPage={subsRowsPerPage}
              onRowsPerPageChange={(e) => {
                setSubsRowsPerPage(parseInt(e.target.value));
                setSubsPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{ mt: 2 }}
            />
            <Dialog
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Filter Indemnities</DialogTitle>
              <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <Autocomplete
                  multiple
                  options={submissionFilterOptions.bondTypes}
                  value={filters.bondType}
                  onChange={(_e, v) => {
                    setFilters((prev) => ({ ...prev, bondType: v }));
                    setSubsPage(0);
                  }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                  renderInput={(p) => <TextField {...p} label="Bond Type" />}
                />
                <Autocomplete
                  multiple
                  options={submissionFilterOptions.bondSubCategories}
                  value={filters.bondSubCategory}
                  onChange={(_e, v) => {
                    setFilters((prev) => ({ ...prev, bondSubCategory: v }));
                    setSubsPage(0);
                  }}
                  renderInput={(p) => <TextField {...p} label="Bond Sub Category" />}
                />
                <Autocomplete
                  multiple
                  options={submissionFilterOptions.bondNumbers}
                  value={filters.bondNumber}
                  onChange={(_e, v) => {
                    setFilters((prev) => ({ ...prev, bondNumber: v }));
                    setSubsPage(0);
                  }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                  renderInput={(p) => <TextField {...p} label="Bond Number" />}
                />
                <Autocomplete
                  multiple
                  options={submissionFilterOptions.principals}
                  value={filters.principal}
                  onChange={(_e, v) => {
                    setFilters((prev) => ({ ...prev, principal: v }));
                    setSubsPage(0);
                  }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                  renderInput={(p) => <TextField {...p} label="Principal" />}
                />
                <Autocomplete
                  multiple
                  options={submissionFilterOptions.estates}
                  value={filters.estate}
                  onChange={(_e, v) => {
                    setFilters((prev) => ({ ...prev, estate: v }));
                    setSubsPage(0);
                  }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                  renderInput={(p) => (
                    <TextField {...p} label="Estate / Ward / Trust / Beneficiary" />
                  )}
                />
                <Autocomplete
                  multiple
                  options={submissionFilterOptions.lawFirms}
                  value={filters.lawFirm}
                  onChange={(_e, v) => {
                    setFilters((prev) => ({ ...prev, lawFirm: v }));
                    setSubsPage(0);
                  }}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  )}
                  renderInput={(p) => <TextField {...p} label="Law Firm" />}
                />
                <Autocomplete
                  multiple
                  options={submissionFilterOptions.statuses}
                  value={filters.status}
                  onChange={(_e, v) => {
                    setFilters((prev) => ({ ...prev, status: v }));
                    setSubsPage(0);
                  }}
                  renderInput={(p) => <TextField {...p} label="Status" />}
                />
                <TextField
                  type="date"
                  label="Start Date"
                  value={filters.startDate || ""}
                  onChange={(e) => {
                    const next = { ...filters, startDate: e.target.value };
                    setFilters(next);
                    setSubsPage(0);
                  }}
                  slotProps={{ inputLabel: { shrink: true }}}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={filters.endDate || ""}
                  onChange={(e) => {
                    const next = { ...filters, endDate: e.target.value };
                    setFilters(next);
                    setSubsPage(0);
                  }}
                  slotProps={{ inputLabel: { shrink: true }}}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setFilters({
                      bondNumber: [],
                      bondType: [],
                      bondSubCategory: [],
                      principal: [],
                      estate: [],
                      lawFirm: [],
                      status: [],
                      startDate: "",
                      endDate: "",
                    });
                    setSubsPage(0);
                  }}
                >
                  Clear
                </Button>
                <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={() => setFilterDialogOpen(false)}>
                  Apply
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={detailsOpen}
              onClose={() => setDetailsOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <VisibilityIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Indemnity Details
                </Typography>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 2.5 }}>
                {detailsRow ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Bond Number
                        </Typography>
                        <Typography variant="body2">{detailsRow.bond_number || "-"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Bond Type
                        </Typography>
                        <Typography variant="body2">{detailsRow.bond_type || "-"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Sub Category
                        </Typography>
                        <Typography variant="body2">{detailsRow.bond_sub_category || "-"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Principal(s)
                        </Typography>
                        <Typography variant="body2">
                          {Array.isArray(detailsRow.principals)
                            ? detailsRow.principals.join(", ")
                            : detailsRow.principals || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Law Firm
                        </Typography>
                        <Typography variant="body2">{detailsRow.law_firm_name || "-"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Estate / Ward / Trust / Beneficiary
                        </Typography>
                        <Typography variant="body2">{detailsRow.estate_name || "-"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Requester
                        </Typography>
                        <Typography variant="body2">
                          {(() => {
                            const name =
                              detailsRow.requester_info?.name ||
                              detailsRecord?.requester_info?.name ||
                              "";
                            const email =
                              detailsRow.requester_info?.email ||
                              detailsRecord?.requester_info?.email ||
                              "";
                            if (name && email) return `${name} · ${email}`;
                            if (name) return name;
                            if (email) return email;
                            return "-";
                          })()}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Created At
                        </Typography>
                        <Typography variant="body2">{formatDate(detailsRow.created_at)}</Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Files
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => openFileManageDialog("")}
                        sx={{ textTransform: "none" }}
                      >
                        Add file
                      </Button>
                    </Box>

                    {/* Existing files only */}
                    {detailsRecord ? (
                      (() => {
                        const files = [1, 2, 3, 4]
                          .map((n) => ({
                            slot: n,
                            url: detailsRecord[`file_url_${n}`],
                            name: detailsRecord[`file_name_${n}`],
                          }))
                          .filter((f) => Boolean(f.url));

                        if (!files.length) {
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                              No files uploaded yet.
                            </Typography>
                          );
                        }

                        return (
                          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                            {files.map((f) => (
                              <Paper
                                key={`file-${f.slot}`}
                                elevation={0}
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  border: "1px solid rgba(37, 99, 235, 0.18)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <PictureAsPdfIcon sx={{ color: "error.main" }} />
                                  <Box>
                                    <Typography variant="body2">
                                      {f.name || "indemnity.pdf"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Stored file
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <Tooltip title="Open">
                                    <IconButton
                                      size="small"
                                      onClick={() => window.open(f.url, "_blank", "noopener,noreferrer")}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Replace">
                                    <IconButton size="small" onClick={() => openFileManageDialog(f.slot)}>
                                      <CloudUploadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => openDeleteFileDialog(f.slot)}>
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Paper>
                            ))}
                          </Stack>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        No record loaded.
                      </Typography>
                    )}
                  </>
                ) : null}
              </DialogContent>
              <DialogActions sx={{ justifyContent: "space-between" }}>
                <Button variant="text" onClick={() => setDetailsOpen(false)}>
                  Close
                </Button>
                {canSendToLiberty && getRowStatus(detailsRow) === "pending" ? (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleOpenRejectDialog}
                      disabled={rejectSubmitting}
                    >
                      Reject
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSendToLiberty}
                      disabled={approveSubmitting}
                    >
                      {approveSubmitting ? "Sending..." : "Send to Liberty"}
                    </Button>
                  </Box>
                ) : null}
              </DialogActions>
            </Dialog>

            {/* Upload/Replace File Dialog */}
            <Dialog
              open={fileManageOpen}
              onClose={() => !fileManageSubmitting && setFileManageOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Upload / Replace File</DialogTitle>
              <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Alert severity="info">
                {fileManageSlot
                  ? "You are replacing an existing uploaded file."
                    : "You are adding a new file. It will be stored in the first available space."}
              </Alert>

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  sx={{ textTransform: "none", justifyContent: "flex-start" }}
                  disabled={fileManageSubmitting}
                >
                  Choose PDF
                  <input
                    type="file"
                    hidden
                    accept="application/pdf"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      if (f && !(f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"))) {
                        toast.error("Only PDF files are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setFileManageFile(f);
                    }}
                  />
                </Button>
                {fileManageFile ? (
                  <Typography variant="caption" color="text.secondary">
                    Selected: {fileManageFile.name}
                  </Typography>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setFileManageOpen(false)} disabled={fileManageSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUploadOrReplaceFile}
                  disabled={!fileManageFile || fileManageSubmitting}
                >
                  {fileManageSubmitting ? "Uploading..." : "Upload"}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete File Confirmation Dialog */}
            <Dialog
              open={deleteFileOpen}
              onClose={() => !deleteFileSubmitting && setDeleteFileOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Delete File</DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary">
                This will remove the selected file. You can upload a new file later.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteFileOpen(false)} disabled={deleteFileSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteFileSlot}
                  disabled={deleteFileSubmitting}
                >
                  {deleteFileSubmitting ? "Deleting..." : "Delete"}
                </Button>
              </DialogActions>
            </Dialog>
            {canSendToLiberty && (
              <Dialog
                open={rejectDialogOpen}
                onClose={() => !rejectSubmitting && setRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Reject Indemnity</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Please provide a brief reason for rejecting this indemnity. This reason may be
                    shared with the submitting user.
                  </Typography>
                  <TextField
                    label="Rejection Reason"
                    multiline
                    minRows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    fullWidth
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setRejectDialogOpen(false)} disabled={rejectSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleSubmitReject}
                    disabled={rejectSubmitting}
                  >
                    {rejectSubmitting ? "Rejecting..." : "Reject"}
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default ManuallySignedIndemnities;


